import { Plus, Trash2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { SettingsRow, TariffType } from "@/lib/api"
import {
  estimateMonthlyKwh,
  periodPrices,
  type ConsumptionMonth,
  type PeriodKwh,
} from "@/lib/consumption"
import { cn } from "@/lib/utils"

// A repartição do consumo é SEMPRE 3-way (Vazio/Ponta/Cheias),
// independente do tarifário — é o que alimenta o gráfico e o dimensionamento.
// O preço por período é que varia com o tarifário (ver periodPrices).
const CONSUMPTION_PERIODS: Array<{ key: keyof PeriodKwh; label: string }> = [
  { key: "empty", label: "Vazio" },
  { key: "peak", label: "Ponta" },
  { key: "full", label: "Cheias" },
]

const MONTHS_PT = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
]

export interface ConsumptionCardProps {
  settings: SettingsRow | null
  tariffType: TariffType
  onTariff: (v: TariffType) => void
  hasInvoice: boolean
  onHasInvoice: (v: boolean) => void
  billEur: string
  onBill: (v: string) => void
  /** Distribuição (fracções) usada na estimativa quando não há fatura. */
  dist: PeriodKwh
  onDist: (v: PeriodKwh) => void
  /** Meses reais quando há fatura. */
  months: ConsumptionMonth[]
  onMonths: (v: ConsumptionMonth[]) => void
}

export function ConsumptionCard({
  settings,
  tariffType,
  onTariff,
  hasInvoice,
  onHasInvoice,
  billEur,
  onBill,
  dist,
  onDist,
  months,
  onMonths,
}: ConsumptionCardProps) {
  const periods = CONSUMPTION_PERIODS
  const fixedFrac = Number(settings?.estimate_fixed_cost_fraction ?? 0.26)
  const prices = settings
    ? periodPrices(settings, tariffType)
    : { empty: 0, peak: 0, full: 0 }

  // Estimativa (sem fatura): kWh/mês por período a partir do valor da conta.
  const estimated = estimateMonthlyKwh(Number(billEur) || 0, fixedFrac, dist, prices)
  const distSumPct = Math.round(
    periods.reduce((s, p) => s + (Number(dist[p.key]) || 0), 0) * 100,
  )

  function setDistPct(key: keyof PeriodKwh, pct: number) {
    onDist({ ...dist, [key]: Math.max(0, pct) / 100 })
  }

  function patchMonth(i: number, key: keyof PeriodKwh, val: number) {
    onMonths(months.map((m, idx) => (idx === i ? { ...m, [key]: Math.max(0, val) } : m)))
  }
  function addMonth() {
    if (months.length >= 12) return
    onMonths([...months, { month: months.length + 1, empty: 0, peak: 0, full: 0 }])
  }
  function removeMonth(i: number) {
    if (months.length <= 3) return
    onMonths(months.filter((_, idx) => idx !== i))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consumos</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Tarifário</Label>
            <Select value={tariffType} onValueChange={(v) => onTariff(v as TariffType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="simples">Simples</SelectItem>
                <SelectItem value="bi_horaria">Bi-horário</SelectItem>
                <SelectItem value="tri_horaria">Tri-horário</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Valor médio mensal (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={billEur}
              onChange={(e) => onBill(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div className="pr-4">
            <Label htmlFor="has-invoice">Tem fatura (com consumos reais)?</Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Não → estimamos a partir do valor. Sim → preenche os kWh reais por
              período, mês a mês.
            </p>
          </div>
          <Switch id="has-invoice" checked={hasInvoice} onCheckedChange={onHasInvoice} />
        </div>

        {!hasInvoice ? (
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <Label>Repartição do consumo (%)</Label>
              <span
                className={cn(
                  "rounded px-2 py-0.5 text-xs font-semibold",
                  distSumPct === 100
                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
                )}
              >
                Total: {distSumPct}%
              </span>
            </div>
            {
              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${periods.length}, minmax(0, 1fr))` }}>
                {periods.map((p) => (
                  <div key={p.key} className="space-y-1">
                    <Label className="text-xs">{p.label}</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="1"
                        value={Math.round((Number(dist[p.key]) || 0) * 100)}
                        onChange={(e) => setDistPct(p.key, Number(e.target.value) || 0)}
                        className="pr-6 text-right"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        %
                      </span>
                    </div>
                    <p className="text-right text-[11px] text-muted-foreground">
                      {Number(estimated[p.key]).toFixed(0)} kWh
                    </p>
                  </div>
                ))}
              </div>
            }
            <p className="text-xs text-muted-foreground">
              Retira-se {Math.round(fixedFrac * 100)}% de custos fixos ao valor e
              o restante divide-se pelo preço de cada período.
            </p>
          </div>
        ) : (
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between">
              <Label>Consumos reais por mês (kWh)</Label>
              <span className="text-xs text-muted-foreground">
                {months.length} {months.length === 1 ? "mês" : "meses"} · mín. 3
              </span>
            </div>
            <div className="space-y-2">
              {months.map((m, i) => (
                <div key={i} className="flex items-end gap-2">
                  <div className="w-20">
                    {i === 0 && <Label className="text-xs">Mês</Label>}
                    <Select
                      value={String(m.month ?? i + 1)}
                      onValueChange={(v) => patchMonth(i, "month" as never, Number(v) as never)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS_PT.map((mm, idx) => (
                          <SelectItem key={idx} value={String(idx + 1)}>
                            {mm}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {periods.map((p) => (
                    <div key={p.key} className="flex-1">
                      {i === 0 && <Label className="text-xs">{p.label}</Label>}
                      <Input
                        type="number"
                        step="1"
                        value={Number(m[p.key]) || 0}
                        onChange={(e) => patchMonth(i, p.key, Number(e.target.value) || 0)}
                        className="h-9 text-right"
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    aria-label="Remover mês"
                    disabled={months.length <= 3}
                    onClick={() => removeMonth(i)}
                    className="mb-0.5 rounded p-2 text-destructive hover:bg-destructive/10 disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addMonth}
              disabled={months.length >= 12}
            >
              <Plus className="h-4 w-4" />
              Adicionar mês
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
